import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IPortalPageSection } from 'models/portal/page/portalpagesection';
import { IModalAction } from 'models/modal';
import {
	ManageSectionItemArticle,
	ManageSectionItemPoll,
	ManageSectionItemProduct,
	ManageSectionItemProfile,
	ManageSectionItemShop,
} from 'components/PortalPage/SectionItems';
import { PortalSelectMenu } from 'components/FormControls';
import { PortalPageSection } from 'components/PortalStructure';
import { ErrorSummary } from 'components';

type ItemType =
	| typeof c.PORTAL_PAGE_SECTION_ITEM_ARTICLE
	| typeof c.PORTAL_PAGE_SECTION_ITEM_POLL
	| typeof c.PORTAL_PAGE_SECTION_ITEM_PRODUCT
	| typeof c.PORTAL_PAGE_SECTION_ITEM_PROFILE
	| typeof c.PORTAL_PAGE_SECTION_ITEM_SHOP;
const allItemTypes: ItemType[] = [
	c.PORTAL_PAGE_SECTION_ITEM_ARTICLE,
	c.PORTAL_PAGE_SECTION_ITEM_POLL,
	c.PORTAL_PAGE_SECTION_ITEM_PRODUCT,
	c.PORTAL_PAGE_SECTION_ITEM_PROFILE,
	c.PORTAL_PAGE_SECTION_ITEM_SHOP,
];

interface IProps {
	index: number;
	section: IPortalPageSection;
	currentFormValues: IPortalPageSection;
	baseUrl?: string;
	change: (field: any, value: any) => void;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	t?: (key: string) => string;
	disableTypes?: ItemType[];
}

interface IState {
	error: boolean;
	message: any;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class PortalPageSectionItemSelector extends React.Component<IProps, IState> {
	private selectedItemType: string = '';

	constructor(props) {
		super(props);

		this.state = {
			error: false,
			message: null,
		};

		this.setValue(props, false);

		this.onChange = this.onChange.bind(this);
		this.onItemTypeChange = this.onItemTypeChange.bind(this);
		this.setError = this.setError.bind(this);
		this.clearError = this.clearError.bind(this);
	}

	private setValue(props: IProps, updateOnChange = true) {
		const prevSelected = this.selectedItemType;
		if (props.section && props.section.items[props.index]) {
			this.selectedItemType = props.section.items[props.index].type;
		}
		if (this.selectedItemType !== prevSelected && updateOnChange) {
			this.forceUpdate();
		}
	}

	private setError(error: any) {
		this.setState({
			error: true,
			message: error,
		});
	}

	private clearError() {
		this.setState({
			error: false,
			message: null,
		});
	}

	private onChange(field: string, value: any) {
		this.props.change(field, value);
	}

	private getComponent(type: string): JSX.Element {
		const props = this.props;

		switch (type) {
			case c.PORTAL_PAGE_SECTION_ITEM_ARTICLE:
				return <ManageSectionItemArticle {...props} setError={this.setError} clearError={this.clearError} />;
			case c.PORTAL_PAGE_SECTION_ITEM_POLL:
				return <ManageSectionItemPoll {...props} />;
			case c.PORTAL_PAGE_SECTION_ITEM_PRODUCT:
				return <ManageSectionItemProduct {...props} setError={this.setError} clearError={this.clearError} />;
			case c.PORTAL_PAGE_SECTION_ITEM_PROFILE:
				return <ManageSectionItemProfile {...props} setError={this.setError} clearError={this.clearError} />;
			case c.PORTAL_PAGE_SECTION_ITEM_SHOP:
				return <ManageSectionItemShop {...props} setError={this.setError} clearError={this.clearError} />;
			default:
				return null;
		}
	}

	private onItemTypeChange(item: string) {
		this.selectedItemType = item;
		this.onChange('items[' + this.props.index + ']', null);
		this.forceUpdate();
	}

	public render() {
		const { t, disableTypes } = this.props;
		const { error, message } = this.state;

		const disabledTypes = disableTypes || [c.PORTAL_PAGE_SECTION_ITEM_SHOP, c.PORTAL_PAGE_SECTION_ITEM_PROFILE];

		const component = this.getComponent(this.selectedItemType);
		const options: string[] = allItemTypes.filter((opt) => disabledTypes.indexOf(opt) === -1);
		options.unshift('');

		const optionsData = options.map((item: string) => {
			return {
				key: item,
				value: item === '' ? '' : t('PortalPage_SectionItem_' + item),
			};
		});

		const dropDownInputProps: object = {
			id: 'itemtypedropdown',
			onChange: this.onItemTypeChange,
			value: this.selectedItemType,
			name: 'itemtypedropdown',
		};

		const errorSummaryProps = {
			error,
			message,
		};

		return (
			<div>
				<div>
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<PortalPageSection usage="admin">
					<PortalSelectMenu
						usage="admin"
						input={dropDownInputProps}
						items={optionsData}
						label={t('PortalPage_SectionItemSelector_Label_SelectType')}
					/>
				</PortalPageSection>
				{component}
			</div>
		);
	}
}

export { PortalPageSectionItemSelector };
